import React, { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

const COLORS = ["#FFB9A3", "#B8F0D2", "#FFE27A", "#A9D4FF", "#FFB3D1", "#8E7BE0"];

const burst = () =>
  Array.from({ length: 10 }, (_, index) => {
    const angle = (index / 10) * Math.PI * 2;
    const distance = 26 + (index % 3) * 10;
    return {
      id: `${Date.now()}-${index}`,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      color: COLORS[index % COLORS.length],
    };
  });

const Eye = ({ cx, cy }) => (
  <g transform={`translate(${cx} ${cy})`}>
    <g className="eye">
      <ellipse cx="0" cy="0" rx="8" ry="10" fill="#1A1428" />
      <circle className="pupil-dot" r="3.1" fill="#F7F4FF" />
      <circle cx="-2.5" cy="-3.5" r="2" fill="#FFFFFF" />
    </g>
  </g>
);

const Face = ({ x, y }) => (
  <g className="face" transform={`translate(${x} ${y})`}>
    <Eye cx={-16} cy={0} />
    <Eye cx={16} cy={0} />
    <path
      className="mouth-neutral"
      d="M-14 16 Q0 26 14 16"
      fill="none"
      stroke="#1A1428"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      className="mouth-happy"
      d="M-18 12 Q0 34 18 12"
      fill="none"
      stroke="#1A1428"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <circle className="mouth-surprised" cx="0" cy="18" r="7" fill="#1A1428" />
    <circle className="cheek cheek-l" cx="-28" cy="14" r="6" fill="#FFB3D1" opacity="0.4" />
    <circle className="cheek cheek-r" cx="28" cy="14" r="6" fill="#FFB3D1" opacity="0.4" />
  </g>
);

const CalBody = () => (
  <>
    <g className="shadow">
      <ellipse cx="100" cy="184" rx="48" ry="8" fill="#0B0B0F" opacity="0.16" />
    </g>
    <g className="body">
      <rect x="48" y="52" width="104" height="112" rx="28" fill="#8E7BE0" />
      <rect x="48" y="118" width="104" height="46" rx="22" fill="#5B4A9C" />
      <ellipse cx="82" cy="78" rx="22" ry="12" fill="#FFFFFF" opacity="0.35" />
      <circle cx="78" cy="52" r="11" fill="none" stroke="#5B4A9C" strokeWidth="7" />
      <circle cx="122" cy="52" r="11" fill="none" stroke="#5B4A9C" strokeWidth="7" />
    </g>
    <g className="arms">
      <ellipse className="arm arm-l" cx="40" cy="112" rx="14" ry="10" fill="#8E7BE0" />
      <ellipse className="arm arm-r" cx="160" cy="112" rx="14" ry="10" fill="#8E7BE0" />
    </g>
    <Face x={100} y={108} />
  </>
);

const ReelBody = () => (
  <>
    <g className="shadow">
      <ellipse cx="100" cy="186" rx="42" ry="8" fill="#0B0B0F" opacity="0.16" />
    </g>
    <g className="body">
      <rect x="64" y="34" width="72" height="132" rx="24" fill="#A9D4FF" />
      <rect x="64" y="118" width="72" height="48" rx="20" fill="#6AA8E0" />
      <ellipse cx="88" cy="58" rx="16" ry="10" fill="#FFFFFF" opacity="0.4" />
      <polygon points="92,88 92,112 114,100" fill="#1A1428" />
    </g>
    <g className="arms">
      <ellipse className="arm arm-l" cx="52" cy="108" rx="13" ry="9" fill="#A9D4FF" />
      <ellipse className="arm arm-r" cx="148" cy="108" rx="13" ry="9" fill="#A9D4FF" />
    </g>
    <Face x={100} y={78} />
  </>
);

const RateBody = () => (
  <>
    <g className="shadow">
      <ellipse cx="100" cy="184" rx="46" ry="8" fill="#0B0B0F" opacity="0.16" />
    </g>
    <g className="body">
      <circle cx="100" cy="104" r="62" fill="#FFE27A" />
      <path d="M48 120 A62 62 0 0 0 152 120 L152 150 A62 62 0 0 1 48 150 Z" fill="#E2C45A" />
      <ellipse cx="80" cy="78" rx="18" ry="10" fill="#FFFFFF" opacity="0.45" />
      <path d="M138 78 L156 96 L138 114 L132 108 L144 96 L132 84 Z" fill="#E2C45A" />
    </g>
    <g className="arms">
      <ellipse className="arm arm-l" cx="36" cy="112" rx="14" ry="10" fill="#FFE27A" />
      <ellipse className="arm arm-r" cx="164" cy="112" rx="14" ry="10" fill="#FFE27A" />
    </g>
    <Face x={96} y={100} />
  </>
);

const PipBody = () => (
  <>
    <g className="shadow">
      <ellipse cx="100" cy="184" rx="40" ry="8" fill="#0B0B0F" opacity="0.16" />
    </g>
    <g className="body">
      <path
        d="M100 36 L118 78 L164 84 L130 114 L140 160 L100 136 L60 160 L70 114 L36 84 L82 78 Z"
        fill="#FFB9A3"
      />
      <ellipse cx="86" cy="78" rx="14" ry="8" fill="#FFFFFF" opacity="0.4" />
    </g>
    <g className="arms">
      <ellipse className="arm arm-l" cx="48" cy="118" rx="12" ry="8" fill="#FFB9A3" />
      <ellipse className="arm arm-r" cx="152" cy="118" rx="12" ry="8" fill="#FFB9A3" />
    </g>
    <Face x={100} y={104} />
  </>
);

const BODIES = {
  cal: CalBody,
  reel: ReelBody,
  rate: RateBody,
  pip: PipBody,
};

const LABELS = {
  cal: "Cal, the calendar mascot",
  reel: "Reel, the player mascot",
  rate: "Rate, the coin mascot",
  pip: "Pip, the star mascot",
};

const Character = ({
  variant,
  size = 160,
  label,
  decorative = false,
  icon = false,
  mood,
  className = "",
}) => {
  const reduced = useReducedMotion();
  const svgRef = useRef(null);
  const [local, setLocal] = useState("neutral");
  const [bits, setBits] = useState([]);
  const Body = BODIES[variant];
  const expr = mood || local;

  useEffect(() => {
    const node = svgRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => node.classList.toggle("is-paused", !entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const onClick = () => {
    if (decorative || icon || reduced) return;
    setLocal("surprised");
    setBits(burst());
    window.setTimeout(() => setBits([]), 600);
    window.setTimeout(() => setLocal((value) => (value === "surprised" ? "happy" : value)), 600);
  };

  const svg = (
    <svg
      ref={svgRef}
      viewBox="0 0 200 200"
      width={size}
      height={size}
      data-look={decorative ? undefined : "true"}
      data-variant={variant}
      data-expr={expr}
      data-icon={icon ? "true" : undefined}
      aria-hidden="true"
      className="block overflow-visible"
    >
      <g className="char">
        <Body />
      </g>
    </svg>
  );

  if (decorative || icon) {
    return <span className={`inline-flex ${className}`}>{svg}</span>;
  }

  return (
    <button
      type="button"
      aria-label={label || LABELS[variant]}
      className={`char-hit relative inline-flex appearance-none border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent ${className}`}
      onMouseEnter={() => {
        if (!mood) setLocal("happy");
      }}
      onMouseLeave={() => {
        if (!mood) setLocal("neutral");
      }}
      onClick={onClick}
    >
      {svg}
      {bits.map((bit) => (
        <span
          key={bit.id}
          className="confetti-bit"
          style={{
            background: bit.color,
            "--cx": `${bit.x}px`,
            "--cy": `${bit.y}px`,
          }}
        />
      ))}
    </button>
  );
};

export const Cal = (props) => <Character variant="cal" {...props} />;
export const Reel = (props) => <Character variant="reel" {...props} />;
export const Rate = (props) => <Character variant="rate" {...props} />;
export const Pip = (props) => <Character variant="pip" {...props} />;

export default Character;
